import {
  Check,
  Cpu,
  Fan,
  HourglassMedium,
  HouseLine,
  type Icon,
  Question,
  SquareHalf,
  ThermometerHot,
  Wind,
} from "@phosphor-icons/react";
import { useLayoutEffect } from "react";

import { type OnboardingState, type OnboardingStepState } from "@/lib/api";
import { spellCount } from "@/lib/spell-count";
import { cn } from "@/lib/utils";

import { useAssistant } from "./assistant-context";
import { CARD_META, CLOSING_TASK, MODULE_KEYS, type ModuleKey } from "./compartments";
import { PillButton } from "./components";

/**
 * Flow steps a row covers *besides* its own key. Only the hub spans more than
 * one: its five dots run through network prep (`network_setup`) before the hub
 * itself connects, and network prep has no row of its own.
 *
 * Tapping a row resumes at the module's first unfinished step, so this is also
 * what decides "START HERE" vs "CONTINUE HERE" — see `isPartlyDone` below.
 */
const MODULE_PREREQ_STEPS: Record<string, readonly string[]> = {
  home_assistant: ["network_setup"],
};

// Phosphor icons, light weight — the design system's icon set (lucide reads
// too bold next to it). Migrate other screens as they get their redesigns.
const MODULE_ICONS: Record<string, Icon> = {
  home_assistant: Cpu,
  air_sensors: Wind,
  floor_heating: ThermometerHot,
  ventilation: Fan,
  windows: SquareHalf,
};

/**
 * Progressive tense per module, for the active row only.
 *
 * Upcoming and finished rows read as plain labels; the active one reads as
 * something under way ("Pairing VELUX Windows"), which is the tense shift the
 * frame draws between its active card and the dimmed ones. Lives here rather
 * than in `compartments.ts` because it is this screen's device: the printed kit
 * sheet uses the imperative `verb` instead, since nothing is under way on paper.
 */
const MODULE_PROGRESSIVE: Record<ModuleKey, string> = {
  home_assistant: "Setting up",
  air_sensors: "Setting up",
  windows: "Pairing",
  floor_heating: "Connecting",
  ventilation: "Connecting",
};

function isDone(step: OnboardingStepState | undefined): boolean {
  return step?.status === "completed" || step?.status === "skipped";
}

type RowState = "active" | "upcoming" | "done" | "skipped";

interface ModuleRowProps {
  step: OnboardingStepState;
  order: number;
  state: RowState;
  /** Active rows only: some of the module's steps are already finished. */
  partlyDone: boolean;
  onOpen: () => void;
}

/**
 * One module in the guide's list — the same row shape in three states.
 *
 * The list never reorders. Its order is the kit box in the user's hands, so a
 * finished module stays in its compartment's slot and simply changes state;
 * progress reads as how far the checks have travelled down the list.
 */
function ModuleRow({ step, order, state, partlyDone, onOpen }: ModuleRowProps) {
  const meta = CARD_META[step.key as ModuleKey];
  const label = meta?.label ?? step.title;
  const ModuleIcon = MODULE_ICONS[step.key] ?? Cpu;
  // Only the active row carries a verb. The frame puts one on every row
  // ("Pair Velux Windows"), but that was drawn before the naming lock and
  // without the hub ever appearing as a dimmed row: at the frame's own 20px
  // and 96px reserved column, "Set up SmartClimate Hub" needs 227px of the
  // 187px available and "Pair VELUX Windows" 194px. Dropping to 18px still
  // overflows. Naming the module alone fits every row at full size, keeps the
  // titles aligned with the active one, and reads the way a checklist should —
  // the thing you are doing is phrased as an action, the rest are just labels.
  const progressive = MODULE_PROGRESSIVE[step.key as ModuleKey];
  const title = state === "active" && progressive ? `${progressive} ${label}` : label;

  // The 96px column is reserved on every row, filled only on the active one,
  // so every title starts at the same x whatever state its row is in.
  const badge = (
    <span
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-lg text-[9px] leading-none",
        state === "active" && "bg-accent text-accent-foreground",
        state === "upcoming" && "bg-surface-subtle text-accent-foreground",
        state === "done" && "bg-success text-accent-foreground",
        // Yellow, and the order number rather than a check. The check is what
        // VLX-76 is about: a skipped module wearing one claims credit for
        // hardware still in the box. The number keeps the row addressable
        // ("compartment 4") when support asks what was skipped.
        state === "skipped" && "bg-daylight text-primary",
      )}
    >
      {state === "done" ? <Check weight="bold" className="size-2.5" /> : order}
    </span>
  );

  if (state === "active") {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="border-accent bg-card flex h-[121px] w-full items-center gap-[18px] overflow-hidden rounded-r-lg border-l-4 pt-3 pr-3 pb-[18px] pl-3.5 text-left shadow-[0px_4px_3.6px_0px_rgba(0,0,0,0.1)]"
      >
        <div className="flex h-full w-24 shrink-0 flex-col items-start justify-between">
          <div className="flex w-full items-start justify-between">
            {badge}
            <span className="bg-surface-strong type-eyebrow text-primary rounded-sm px-1.5 py-0.5">
              {partlyDone ? "CONTINUE HERE" : "START HERE"}
            </span>
          </div>
          {/* The tile is not in the frame, but three of the five products are
              near-white plastic on a white cutout — the VELUX Window Connector
              is 1.2% non-white pixels, so on the card's white it reads as an
              empty box. `bg-background` behind it is the same treatment
              the row list uses. */}
          <div className="bg-background flex h-14 w-24 items-center justify-center rounded-lg">
            <img src={meta?.image} alt="" className="h-12 w-20 object-contain" />
          </div>
        </div>
        <div className="flex h-full min-w-0 flex-1 flex-col justify-between">
          <div className="flex items-start justify-between">
            <span className="text-muted type-caption flex items-center gap-1">
              <HourglassMedium weight="light" className="size-4" />
              {meta?.minutes}min
            </span>
            <ModuleIcon weight="light" className="text-primary size-6" />
          </div>
          <p className="text-primary type-subhead">{title}</p>
        </div>
      </button>
    );
  }

  const done = state === "done";
  const skipped = state === "skipped";
  return (
    <button
      type="button"
      onClick={done || skipped ? onOpen : undefined}
      disabled={!done && !skipped}
      aria-current={undefined}
      // Neutral resting surface for every row (the per-module tint experiment
      // read washed-out here — Nico reverted it; the tints live on inside each
      // module's own hero block). The one exception is a skipped row, which
      // wears the brand's Background Yellow: skipped is a state the household
      // should notice every time they pass this screen, not a variant of done.
      // It stays tappable because reopening it is how the skip gets undone.
      className={cn(
        "flex h-[59px] w-full items-center gap-[18px] rounded-r-lg px-3 py-3 pl-3.5 text-left",
        skipped ? "bg-daylight-subtle" : "bg-background",
      )}
    >
      {/* Badge centred against the title, not pinned to the top of the column.
          The frame top-aligns it because the active card stacks an image
          underneath; these rows render no image, so a top-aligned badge sat
          9.5px above the title's centre line with nothing below to justify it. */}
      <div className="flex h-full w-24 shrink-0 items-center">{badge}</div>
      {/* `text-muted`, not `text-faint`, for the upcoming rows. These are
          disabled controls, so WCAG 1.4.3 does not hold them to 4.5:1 — but
          faint lands at 1.37–1.59:1 on the module tints, which is past dimming
          and into unreadable, and the token is documented as decorative-only. */}
      <p className={cn("type-subhead truncate", done || skipped ? "text-primary" : "text-muted")}>
        {title}
      </p>
      {/* text-warning, not text-daylight: the CTA yellow lands at 1.6:1 on
          this tint and the darkened warning token is the one documented for
          AA text on yellow surfaces. */}
      {skipped && <span className="text-warning type-caption ml-auto shrink-0">Skipped</span>}
    </button>
  );
}

interface HomeHubScreenProps {
  state: OnboardingState;
  busy: boolean;
  error: string;
  onOpenModule: (stepKey: string) => void;
  /** Opens the closing task (Part B of Air Quality) once the compartments are done. */
  onOpenClosingTask: () => void;
  /**
   * Whether the AirThings cloud has confirmed the sensors are reporting, learned
   * by the background poll in `FlowShell` while the household works through the
   * other compartments. Informational only — it never gates the closing task.
   */
  sensorsReporting?: boolean;
  onFinish: () => void;
}

/**
 * Figma 1175:1224 — "Hello {name}, this is your guide": the module overview as
 * a single vertical list, one row per kit compartment, in the enforced install
 * order.
 *
 * Replaces the earlier card grid (two tall cells, then a compact row of three),
 * which had to fold finished modules into pills to keep the active cell full
 * size — and still cramped a long title like "VELUX Windows" into two lines.
 * One row per module removes both problems and leaves the order intact, which
 * matters because the order is the physical box.
 *
 * The list scrolls under a fixed CTA rather than sitting in one static column:
 * five rows plus the header already overflow a 667px phone, and Air Quality is
 * about to split into two compartments.
 */
export function HomeHubScreen({
  state,
  busy,
  error,
  onOpenModule,
  onOpenClosingTask,
  sensorsReporting = false,
  onFinish,
}: HomeHubScreenProps) {
  const byKey = new Map(state.steps.map((step) => [step.key, step]));
  const modules = MODULE_KEYS.map((key) => byKey.get(key)).filter(
    (step): step is OnboardingStepState => step !== undefined,
  );
  const activeModule = modules.find((step) => !isDone(step));
  const doneCount = modules.filter((step) => isDone(step)).length;
  const skippedCount = modules.filter((step) => step.status === "skipped").length;
  const remainingCount = modules.length - doneCount;

  /**
   * Time copy, derived from the cards rather than written down.
   *
   * Taken from @francisverhoeven-boi's #67, which is landing right after this
   * and owns the copy on this screen. Adopted here so the two do not have to be
   * reconciled during his rebase. A fixed string was wrong twice: wrong for a
   * household with a system switched off, because `applicable_steps()` drops
   * that compartment and the total shrinks with it, and wrong on every visit
   * after the first, when the number that decides whether someone carries on is
   * how long is LEFT, not how long the whole job takes.
   *
   * The closing task adds its own minutes on top, for as long as it is pending.
   */
  const minutesOf = (steps: OnboardingStepState[]) =>
    steps.reduce((sum, step) => sum + (CARD_META[step.key as ModuleKey]?.minutes ?? 0), 0);
  /**
   * Two different questions, and conflating them makes the clock lie.
   *
   * `hasClosingTask` — does this household have air quality at all? That is
   * what decides whether the closing task's minutes belong in the estimate, and
   * they belong there from the first screen: the work is coming either way, and
   * a total that grows by 15 minutes halfway through is worse than one that was
   * honest up front.
   *
   * `closingTaskVisible` — is it worth showing yet? The two halves of
   * compartment 2 are one job: the sensors cannot be placed until they have
   * been woken and paired in Part A, so the row only appears once that is done.
   * Before then it is an instruction for something the household has no way to
   * act on.
   */
  const hasClosingTask = byKey.has("air_sensors");
  const closingTaskVisible = hasClosingTask && isDone(byKey.get("air_sensors"));
  const closingMinutes = hasClosingTask ? CLOSING_TASK.minutes : 0;
  const totalMinutes = minutesOf(modules) + closingMinutes;
  const remainingMinutes = minutesOf(modules.filter((step) => !isDone(step))) + closingMinutes;
  /** Anything finished means this is a return visit, so the copy changes tense. */
  const started = doneCount > 0;
  /** 1-based position of the active compartment, for the CTA's "compartment N". */
  const activeOrder = activeModule ? modules.indexOf(activeModule) + 1 : modules.length;

  /**
   * Whether this module is part-way through — some of its steps done, the module
   * itself not. Tapping it then resumes at the first unfinished step rather than
   * opening step 1, so the badge reads "CONTINUE HERE" and matches where it
   * goes. (It used to key off `doneModules.length`, which is about *other*
   * modules, so a fresh module could read "CONTINUE HERE" and a part-way one
   * "START HERE".)
   */
  const isPartlyDone = (step: OnboardingStepState): boolean =>
    (MODULE_PREREQ_STEPS[step.key] ?? []).some((key) => isDone(byKey.get(key)));

  const assistant = useAssistant();

  // This screen renders no AppBar — its header is the bespoke sunrise halo
  // above — so it doesn't inherit that component's scroll reset. It mounts
  // fresh every time the flow lands on the guide (see `flow-shell.tsx`'s
  // `step()`), and the window may still be scrolled from whatever long module
  // stage was on screen before, so reset it directly — before paint, so the
  // guide never flashes in mid-scroll.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="mx-auto flex h-[100dvh] w-full max-w-[390px] flex-col bg-white">
      {/* The guide's header band. Was the Figma halo asset, a soft sunrise wash
          that needed its own mask to stop the SVG's clipped falloff drawing a
          seam against the white below; it is a flat
          Background-Yellow block now, which needs neither. The band keeps the
          frame's 121px height and the app bar still sits over it.

          White, not a tint: Nico tried the Background-Yellow band here and
          called it back to plain page white — the guide's colour should come
          from its content, not its chrome. And with the halo art gone, the
          frame's 121px band height lost its reason too: 56px is the app bar
          itself, and the 65px saved is what lets the five-module list clear
          the fold on the pilot phones. */}
      <div className="relative h-14 w-full shrink-0 overflow-hidden bg-white">
        <div className="absolute inset-x-0 top-0 flex h-14 items-center justify-between px-[18px]">
          <HouseLine weight="light" className="text-muted size-6" aria-hidden />
          <button
            type="button"
            onClick={() => assistant.open({ stepKey: activeModule?.key ?? "guide" })}
            aria-label="Get help with your setup"
          >
            <Question weight="light" className="text-muted size-6" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-6 py-5">
        <div className="flex flex-col gap-3">
          {/* No "Hello" here, and no fixed duration. Both come from #67, which
              owns the copy on this screen: the greeting screen already says
              hello one tap earlier, and this screen's job is answering "where
              am I". Nico's frame draws the greeting here; the copy call is
              Francis's, so his version wins and is adopted ahead of his rebase
              rather than being reverted during it. */}
          {/* "One last thing" rather than #67's "All done" when the closing task
              is still pending. That PR could say "All done" because its bottom
              detail panel explained the sensors right underneath; this layout
              has no panel, so the title was the only thing left to carry it, and
              "All done" above a CTA reading "Place your air sensors" contradicts
              itself. The words are Francis's own eyebrow from that row. */}
          <h1 className="text-primary type-title">
            {activeModule
              ? started
                ? `${spellCount(remainingCount)} compartment${remainingCount === 1 ? "" : "s"} to go`
                : "Your setup"
              : hasClosingTask
                ? "One last thing"
                : skippedCount > 0
                  ? "Done, with skips"
                  : "All done"}
          </h1>
          <p className="text-muted text-[17px] leading-6">
            {activeModule
              ? started
                ? `About ${remainingMinutes} min left. You can pause at any point and pick up where you left off.`
                : `${spellCount(modules.length)} compartment${modules.length === 1 ? "" : "s"}, in order. About ${totalMinutes} min in total. You can pause at any point and pick up where you left off.`
              : hasClosingTask
                ? skippedCount > 0
                  ? "The compartments are done, except the skipped ones below. Your sensors have been pairing all this time, so now they can go in their rooms."
                  : "Every compartment is done. Your sensors have been pairing all this time, so now they can go in their rooms."
                : skippedCount > 0
                  ? "The yellow compartments were skipped and are not running. Finish to hand the rest over to SmartClimate."
                  : "Every compartment is set up. Finish to hand your home over to SmartClimate."}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {modules.map((step, index) => (
            <ModuleRow
              key={step.key}
              step={step}
              order={index + 1}
              state={
                step.status === "skipped"
                  ? "skipped"
                  : isDone(step)
                    ? "done"
                    : step.key === activeModule?.key
                      ? "active"
                      : "upcoming"
              }
              partlyDone={isPartlyDone(step)}
              onOpen={() => onOpenModule(step.key)}
            />
          ))}

          {/* The closing task is the same row as a compartment, on purpose.
              It is not compartment 6 — no number badge, and the reserved column
              stays empty, which is what says so. But everything measurable about
              it matches: 59px tall, the flat left edge every row shares so the
              active spine has something to sit against, and its title on the
              same 128px baseline as the five above it.

              No duration here either. Only the tall active card carries one; a
              59px row never has, and the header already gives the total. Losing
              it is also what makes the aligned title fit: "Place your air
              sensors" needs 198px, and the content column is 202px.

              Unlocked, it borrows the active row's language rather than
              inventing a treatment — white card, orange left spine, same shadow
              — because when it unlocks it IS the next action. */}
          {closingTaskVisible && (
            <button
              type="button"
              onClick={activeModule ? undefined : onOpenClosingTask}
              disabled={!!activeModule}
              className={cn(
                // min-h, not fixed h: the full label must always read — the
                // card grows to a second line rather than clipping (Nico's
                // call, replacing the earlier clip-in-review approach).
                "flex min-h-[59px] w-full items-center gap-[18px] rounded-r-lg py-3 pr-3 pl-3.5 text-left",
                // Locked, it rests on the same neutral as the module rows.
                activeModule
                  ? "bg-background"
                  : "border-accent bg-card border-l-4 pl-[10px] shadow-[0px_4px_3.6px_0px_rgba(0,0,0,0.1)]",
              )}
            >
              {/* The sensors themselves, not the connector — that image belongs
                  to compartment 2's card; this task is about placing what came
                  out of it. Sized to the 59px row, dimmed while locked. */}
              <div
                className={cn(
                  "bg-background flex h-[43px] w-24 shrink-0 items-center justify-center rounded-lg",
                  activeModule && "opacity-60",
                )}
                aria-hidden
              >
                <img
                  src="/brand/onboarding/airquality-sensors.png"
                  alt=""
                  className="h-9 w-20 object-contain"
                />
              </div>
              {/* `flex-1` so the column claims the width left over, and
                  `whitespace-nowrap` so a hair's overflow shows up as a visible
                  clip in review rather than silently wrapping to a second line
                  and bursting the fixed 59px height. */}
              <span className="flex min-w-0 flex-1 flex-col">
                {/* The eyebrow doubles as the sensor readout. It is the one line
                    of spare room on the row, and "they are reporting" is worth
                    more to someone about to walk the house than a second copy
                    of the label above it. Never a lock: an unconfirmed reading
                    still opens, because Part B is where the escapes live. */}
                <span
                  className={cn(
                    "type-eyebrow",
                    activeModule ? "text-faint" : sensorsReporting ? "text-success" : "text-accent",
                  )}
                >
                  {activeModule
                    ? "ONE LAST THING"
                    : sensorsReporting
                      ? "SENSORS ARE REPORTING"
                      : "ONE LAST THING"}
                </span>
                <span className={cn("type-subhead", activeModule ? "text-faint" : "text-primary")}>
                  {CLOSING_TASK.label}
                </span>
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-3 px-7 pt-4 pb-8">
        {error && (
          <p role="alert" className="text-destructive text-center text-[15px]">
            {error}
          </p>
        )}
        {/* #67's labels. The active one names the compartment rather than a
            generic "install it", because the box is the spine of the flow and
            every pointer back to it earns its keep; "Continue" on a return visit
            also stops the CTA contradicting the CONTINUE HERE badge above it.
            And with compartments done there is still the closing task, so it
            must not read "Finish" while four sensors sit beside the connector. */}
        <PillButton
          label={
            activeModule
              ? `${started ? "Continue" : "Start"} with compartment ${activeOrder}`
              : hasClosingTask
                ? CLOSING_TASK.label
                : "Finish setup"
          }
          busy={busy}
          onClick={() => {
            if (activeModule) {
              onOpenModule(activeModule.key);
            } else if (hasClosingTask) {
              onOpenClosingTask();
            } else {
              onFinish();
            }
          }}
        />
      </div>
    </div>
  );
}
